import { useCallback, useEffect, useRef } from "react";
import { useConversation } from "@elevenlabs/react";
import { PhoneCall, PhoneOff, Loader2 } from "lucide-react";
import { isFeatureEnabled } from "../../config/featureFlags";
import { getUser } from "../../helpers/utils";
import {
  playEndChime,
  playStartChime,
  startThinkingSound,
  stopThinkingSound,
} from "./thinkingSound";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://api.autumnplatform.com"
).replace(/\/$/, "");

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
}

// Short-lived token from our backend — the ElevenLabs key never reaches the browser.
async function fetchVoiceToken(): Promise<string> {
  const res = await fetch(`${API_BASE}/ai/voice-token`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`voice-token ${res.status}`);
  const data = await res.json();
  if (!data.token) throw new Error("voice-token missing");
  return data.token as string;
}

// The voice agent's `ask_assistant` tool runs here, in the browser, with the
// user's JWT — so it routes through the ChatV2 brain over that hotel's data.
async function askAssistant(question: string): Promise<string> {
  // The backend round trip is the multi-second silent gap — play the thinking
  // cue for exactly its duration so the user knows we're working, not broken.
  startThinkingSound();
  try {
    const res = await fetch(`${API_BASE}/ai/voice-answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ question }),
    });
    if (!res.ok) throw new Error(`voice-answer ${res.status}`);
    const data = await res.json();
    return (
      data.answer ||
      "I couldn't find an answer to that — want to try rephrasing?"
    );
  } catch (err) {
    console.error("[chat-voice] ask_assistant failed", err);
    return "Sorry, I couldn't pull that up just now. Please try again in a moment.";
  } finally {
    stopThinkingSound();
  }
}

// The voice agent's `submit_support_ticket` tool runs here, in the browser,
// with the user's JWT — filing through the same support pipeline as the in-app
// help widget (Slack thread + reply routing), so the user never has to click.
async function submitSupportTicket(args: {
  summary: string;
  details?: string;
}): Promise<string> {
  const summary = args.summary?.trim();
  if (!summary) {
    return "I didn't catch what the issue was — could you say that again?";
  }
  try {
    const message = args.details?.trim()
      ? `${summary}\n\n${args.details.trim()}`
      : summary;
    const res = await fetch(`${API_BASE}/support-tickets`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({
        message,
        subject: summary.slice(0, 140),
        source: "voice",
      }),
    });
    if (!res.ok) throw new Error(`support-ticket ${res.status}`);
    return "Done — I've sent that to our team. They'll follow up by email.";
  } catch (err) {
    console.error("[chat-voice] submit_support_ticket failed", err);
    return "I couldn't submit that just now — mind trying again in a moment?";
  }
}

/**
 * Native "Talk" button rendered inside the chat input bar. Opens a live
 * ElevenLabs voice session (ElevenLabs React SDK) using a backend-minted token,
 * and routes every question through the ChatV2 brain via `ask_assistant`.
 * Self-gates to null unless the `chatVoice` flag is on. Requires a
 * <ConversationProvider> ancestor (added in chatv2/index.tsx).
 */
export function ChatTalkButton() {
  const user = getUser();
  const conversation = useConversation({
    clientTools: {
      ask_assistant: async ({ question }: { question: string }) =>
        askAssistant(question),
      submit_support_ticket: async (args: {
        summary: string;
        details?: string;
      }) => submitSupportTicket(args),
    },
    onError: (message: string) => console.error("[chat-voice]", message),
  });

  const { status } = conversation;
  const active = status === "connected";
  const connecting = status === "connecting";
  const isSpeaking = conversation.isSpeaking;

  // Armed while we're bridging the gap between the click and the agent's first
  // words, so the ambient pad fills that silence and stops the moment it greets.
  const fillingGapRef = useRef(false);

  const start = useCallback(async () => {
    // Immediate audible feedback that the click landed — the connection itself
    // takes a moment, so without this the first press feels like nothing happened.
    playStartChime();
    // Keep a soft ambient going through the connect + greeting wait so the gap
    // between the click and the agent's first words isn't dead silence.
    startThinkingSound();
    fillingGapRef.current = true;
    try {
      const conversationToken = await fetchVoiceToken();
      conversation.startSession({
        conversationToken,
        connectionType: "webrtc",
        dynamicVariables: {
          first_name: user?.firstName || "there",
          hotel_name: user?.hotelName || "your hotel",
        },
      });
    } catch (err) {
      console.error("[chat-voice] failed to start voice session", err);
      fillingGapRef.current = false;
      stopThinkingSound();
    }
  }, [conversation, user]);

  // Stop the connecting ambient the instant the agent starts its greeting.
  useEffect(() => {
    if (fillingGapRef.current && isSpeaking) {
      fillingGapRef.current = false;
      stopThinkingSound();
    }
  }, [isSpeaking]);

  // If the call ends or drops before the agent speaks, don't leave it humming.
  useEffect(() => {
    if (!active && !connecting && fillingGapRef.current) {
      fillingGapRef.current = false;
      stopThinkingSound();
    }
  }, [active, connecting]);

  if (!isFeatureEnabled("chatVoice")) return null;

  const endCall = () => {
    fillingGapRef.current = false;
    stopThinkingSound();
    playEndChime();
    conversation.endSession();
  };

  const onClick = active || connecting ? endCall : start;
  const speaking = active && isSpeaking;

  // Icon-only voice button with an ElevenLabs-style grainy gradient orb
  // background (.d-talk-orb in dashboard-tokens.css). Square (w-8 h-8) to match
  // the Send/Mic buttons; data-active drives the in-call "breathing". The state
  // is conveyed by the icon (call / end-call / connecting) + aria-label/title.

  return (
    <button
      type="button"
      onClick={onClick}
      data-tutorial="chat-talk"
      data-active={active || connecting ? "true" : "false"}
      aria-label={active ? "End voice call" : "Talk to us — start a voice call"}
      title={active ? "End voice call" : "Talk to us"}
      className="d-talk-orb inline-flex items-center justify-center w-8 h-8 rounded-[10px] transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--d-accent-deep)] focus-visible:ring-offset-1"
    >
      {connecting ? (
        <Loader2 className="w-[16px] h-[16px] animate-spin" />
      ) : active ? (
        <PhoneOff
          className={`w-[16px] h-[16px] ${speaking ? "animate-pulse" : ""}`}
        />
      ) : (
        <PhoneCall className="w-[16px] h-[16px]" />
      )}
    </button>
  );
}
